import { useEffect, useId, useRef, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { copyTextToClipboard } from '@/lib/copyToClipboard';

import { fetchAllTranscriptSegments, type ChatGptPromptId } from '../api';
import {
  ANALYSIS_PROMPTS,
  buildPromptTranscriptExport,
  type MeetingExportContext,
} from '../buildTranscriptExport';
import { resolveDefaultAnalysisPrompt } from '../defaultAnalysisPrompt';

export type SelectedAnalysisPrompt = {
  id: ChatGptPromptId;
  label: string;
};

type WizardStep = 'select' | 'transfer' | 'return';

type Props = {
  open: boolean;
  onClose: () => void;
  transcriptionId: string;
  meeting: MeetingExportContext;
  meetingType: string | null;
  onAttachResult: (prompt: SelectedAnalysisPrompt) => void;
};

const CHATGPT_URL = 'https://chatgpt.com/';

export function ChatGptAnalysisWizard({
  open,
  onClose,
  transcriptionId,
  meeting,
  meetingType,
  onAttachResult,
}: Props) {
  const listId = useId();
  const busyRef = useRef(false);
  const [step, setStep] = useState<WizardStep>('select');
  const [selectedId, setSelectedId] = useState(() => resolveDefaultAnalysisPrompt(meetingType).id);
  const [error, setError] = useState<string | null>(null);
  const [copying, setCopying] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    setStep('select');
    setSelectedId(resolveDefaultAnalysisPrompt(meetingType).id);
    setError(null);
    setCopying(false);
  }, [open, meetingType]);

  const selected =
    ANALYSIS_PROMPTS.find((prompt) => prompt.id === selectedId) ??
    resolveDefaultAnalysisPrompt(meetingType);

  const subtitle =
    step === 'select'
      ? 'Выберите задание для анализа звонка'
      : step === 'transfer'
        ? 'Скопируйте текст и откройте ChatGPT'
        : 'Вернитесь с ответом и прикрепите результат';

  const onCopyAndOpen = async () => {
    if (busyRef.current) {
      return;
    }
    busyRef.current = true;
    setCopying(true);
    setError(null);
    try {
      const segments = await fetchAllTranscriptSegments(transcriptionId);
      if (segments.length === 0) {
        setError('Текст расшифровки отсутствует');
        return;
      }
      const text = buildPromptTranscriptExport(selected.body, meeting, segments);
      await copyTextToClipboard(text);
      window.open(CHATGPT_URL, '_blank', 'noopener,noreferrer');
      setStep('return');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось скопировать');
    } finally {
      busyRef.current = false;
      setCopying(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Анализ звонка в ChatGPT"
      subtitle={subtitle}
      width="720px"
    >
      <div className="chatgpt-wizard">
        {step === 'select' ? (
          <>
            <fieldset className="chatgpt-wizard__fieldset" aria-labelledby={listId}>
              <legend className="chatgpt-wizard__legend" id={listId}>
                Задание
              </legend>
              <ul className="chatgpt-wizard__prompts">
                {ANALYSIS_PROMPTS.map((prompt) => {
                  const inputId = `${listId}-${prompt.id}`;
                  return (
                    <li key={prompt.id}>
                      <label className="chatgpt-wizard__prompt" htmlFor={inputId}>
                        <input
                          id={inputId}
                          type="radio"
                          name="chatgpt-analysis-prompt"
                          value={prompt.id}
                          checked={selectedId === prompt.id}
                          onChange={() => setSelectedId(prompt.id)}
                        />
                        <span className="chatgpt-wizard__prompt-copy">
                          <span className="chatgpt-wizard__prompt-title">{prompt.label}</span>
                          <span className="chatgpt-wizard__prompt-description">
                            {prompt.description}
                          </span>
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </fieldset>
            <div className="chatgpt-wizard__actions">
              <Button type="button" variant="primary" onClick={() => setStep('transfer')}>
                Далее
              </Button>
              <Button type="button" variant="secondary" onClick={onClose}>
                Отмена
              </Button>
            </div>
          </>
        ) : null}

        {step === 'transfer' ? (
          <>
            <p className="chatgpt-wizard__text">
              Будет скопирован единый текст: сведения о встрече, задание «{selected.label}» и полная
              расшифровка. Затем откроется ChatGPT в новой вкладке.
            </p>
            {error !== null ? (
              <p className="chatgpt-wizard__error" role="alert">
                {error}
              </p>
            ) : null}
            <div className="chatgpt-wizard__actions">
              <Button
                type="button"
                variant="primary"
                disabled={copying}
                onClick={() => {
                  void onCopyAndOpen();
                }}
              >
                {copying ? 'Копирование…' : 'Скопировать и открыть ChatGPT'}
              </Button>
              <Button type="button" variant="secondary" disabled={copying} onClick={() => setStep('select')}>
                Назад
              </Button>
            </div>
          </>
        ) : null}

        {step === 'return' ? (
          <>
            <div className="notice notice--muted">
              <div className="notice__body">
                <p className="notice__title">Вставьте текст в ChatGPT</p>
                <p className="notice__text">
                  Вставьте скопированный текст в ChatGPT, получите ответ и вернитесь сюда.
                </p>
              </div>
            </div>
            <div className="chatgpt-wizard__actions">
              <Button
                type="button"
                variant="primary"
                onClick={() => {
                  onAttachResult({ id: selected.id as ChatGptPromptId, label: selected.label });
                  onClose();
                }}
              >
                Прикрепить результат
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  void onCopyAndOpen();
                }}
              >
                Скопировать ещё раз
              </Button>
            </div>
          </>
        ) : null}
      </div>
    </Modal>
  );
}
